// 🔧 ResponsiveNav.js
// ✅ Purpose: Navigation bar with responsive support and role-aware menus
// ✅ Features:
// - Keeps ^ items always visible outside the hamburger
// - Moves all other nav items into hamburger/collapse based on screen size
// - Supports public, voter, manager, and admin navigation
// - Feature-gates sponsors, vendors, volunteering, auctions, and audit where possible
// - Core package features use requestedModules/fallback booleans
// - Revenue add-ons use purchasedAddOns through hasPurchasedAddOn()
// - Keeps Dashboard global and non-event scoped
// - Keeps Event public and always visible when an event slug exists

import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useRole } from "../role_context";
import { useAuth } from "../auth_context";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useEventContext } from "../hooks/use_event_context";
import { useVotingStatus } from "../hooks/use_voting_status";
import LogoImage from "../assets/images/LetsGOvote-02.png";
import {
  hasPurchasedAddOn,
} from "../utils/event_addons";
import {
  getEventVotingType,
} from "../utils/event_voting_type_helpers";

const ResponsiveNav = () => {
  const { user } = useAuth();
  const { role, eventRole } = useRole();

  const {
    eventId,
    eventslug: activeSlug,
    eventDoc,
  } = useEventContext();

  const location = useLocation();
  const isLanding = location.pathname === "/";
  const [showRecentEvent, setShowRecentEvent] = useState(true);
  const { showLeaderboard } = useVotingStatus(eventId);
  const [hasVotedInThisEvent, setHasVotedInThisEvent] = useState(false);

  const isAdmin = role === "admin";
  const isManager = role === "admin" || eventRole === "manager";

  // ✅ Manager sidebar state
  // Purpose:
  // - Desktop: sidebar is visible when manager/admin and event slug exists
  // - Mobile: sidebar opens/closes from the Manager Menu button
  const [managerSidebarOpen, setManagerSidebarOpen] = useState(false);
  const SIDEBAR_SECTION_STORAGE_KEY = "LGV_SIDEBAR_SECTIONS";

  const defaultSidebarSections = {
    sitePages: true,
    platform: true,
    volunteers: true,
    voting: true,
    sponsors: true,
    vendors: true,
    auctions: true,
    passports: true,
    store: true,
    settings: true,
    engagementTools: true,
    integrations: true,
    promotions: true,
  };

  const [openSidebarSections, setOpenSidebarSections] = useState(() => {
    try {
      const savedSections = localStorage.getItem(SIDEBAR_SECTION_STORAGE_KEY);

      if (!savedSections) {
        return defaultSidebarSections;
      }

      return {
        ...defaultSidebarSections,
        ...JSON.parse(savedSections),
      };
    } catch (err) {
      console.warn("[LGV Nav] Failed to load sidebar section state:", err);
      return defaultSidebarSections;
    }
  });

  const toggleSidebarSection = (sectionKey) => {
    setOpenSidebarSections((prev) => {
      const nextSections = {
        ...prev,
        [sectionKey]: !prev[sectionKey],
      };

      localStorage.setItem(
        SIDEBAR_SECTION_STORAGE_KEY,
        JSON.stringify(nextSections)
      );

      return nextSections;
    });
  };

  const SidebarSectionButton = ({ sectionKey, label }) => (
    <button
      type="button"
      className="lgv-sidebar-section-button"
      onClick={() => toggleSidebarSection(sectionKey)}
    >
      <span>{label}</span>
      <span>{openSidebarSections[sectionKey] ? "▾" : "▸"}</span>
    </button>
  );

  // =====================================================
  // 🧩 Feature / Add-on Helpers
  // =====================================================
  // ✅ Purpose:
  // - Core event package features are available through requestedModules
  // - Revenue add-ons are gated through purchasedAddOns
  // - Backward compatibility is handled by hasPurchasedAddOn()
  // =====================================================

  const requestedModules = Array.isArray(eventDoc?.requestedModules)
    ? eventDoc.requestedModules.map((moduleName) =>
        String(moduleName || "").toLowerCase()
      )
    : [];

  const coreFeatureEnabled = (featureKeys = []) => {
    return featureKeys.some((featureKey) =>
      requestedModules.includes(String(featureKey || "").toLowerCase())
    );
  };

  // =====================================================
  // ✅ Individually Purchased Add-on Gates
  // =====================================================
  // Purpose:
  // - These features appear only when that exact add-on is active
  // - Keeps manager sidebar and public event nav aligned
  // - Prevents add-ons from showing just because an older fallback field exists
  // =====================================================

  const sponsorsEnabled =
    hasPurchasedAddOn(eventDoc, "sponsors") ||
    hasPurchasedAddOn(eventDoc, "sponsor") ||
    hasPurchasedAddOn(eventDoc, "sponsorPayments") ||
    eventDoc?.sponsorsEnabled === true ||
    eventDoc?.sponsorEnabled === true;

  const vendorsEnabled = hasPurchasedAddOn(eventDoc, "vendors");
    
  const auctionEnabled =
    hasPurchasedAddOn(eventDoc, "auction") ||
    hasPurchasedAddOn(eventDoc, "auctions");

  const storeEnabled =
    hasPurchasedAddOn(eventDoc, "store");

  const ticketsEnabled =
    hasPurchasedAddOn(eventDoc, "tickets");

  const passportEnabled =
    hasPurchasedAddOn(eventDoc, "passport");

  // Global admins can inspect an event's Intelligence Dashboard before its
  // Passport Power Up is enabled. Event owners receive this section only with
  // the entitlement, and operational Passport screens remain entitlement-only.
  const passportNavigationVisible = passportEnabled || isAdmin;

  const promotionEnabled =
    hasPurchasedAddOn(eventDoc, "social");

  // Match Social Promotion entitlement checks; legacy root flags grant no access.
  const socialConnectionsEnabled =
    hasPurchasedAddOn(eventDoc, "social") ||
    hasPurchasedAddOn(eventDoc, "promotion");

  // =====================================================
  // ✅ Core / Non-Revenue Feature Gates
  // =====================================================
  // Purpose:
  // - Keep audit as a core/requested feature
  // - Volunteers are included in every event package
  // =====================================================

  const volunteersEnabled = Boolean(eventDoc);

  const auditEnabled =
    coreFeatureEnabled(["auditing", "audit", "reporting"]) ||
    eventDoc?.auditEnabled === true ||
    eventDoc?.auditingEnabled === true ||
    eventDoc?.showAudit === true;

  // =====================================================
  // 🗳️ Voting Engine Helpers
  // =====================================================
  // Purpose:
  // - Use the same canonical voting-type resolver used by
  //   Judge Results and the rest of the voting UI.
  // - Detailed Scorecard uses the production "points"
  //   voting engine rather than a SAP-specific voting type.
  // - Keep judged-result navigation generic for any future
  //   judged/scorecard Experience.
  // =====================================================

  const votingType =
    getEventVotingType(eventDoc);

  // Scoring configuration is available for all voting engines
  // that represent judged / scored ballots.
  const scoringConfigurable =
    votingType === "points" ||
    votingType === "judged" ||
    votingType === "scorecard";

    // =====================================================
    // 🏆 Judge Results Visibility
    // =====================================================
    // Purpose:
    // - Only expose Judge Results for judged voting events
    // - Keeps navigation clean for People's Choice / Ranked Choice
    // - Does not expose SAP-specific behavior
    // =====================================================

  const judgeResultsEnabled =
    votingType === "points" ||
    votingType === "judged" ||
    votingType === "scorecard";
      
  // ==========================================================
  // When entering an event, always allow the
  // recent event banner to be shown again later.
  // ==========================================================
  useEffect(() => {
    if (activeSlug) {
      setShowRecentEvent(true);
    }
  }, [activeSlug]);

  // =====================================================
  // 🗳️ Voted Status
  // =====================================================
  // ✅ Purpose:
  // - Keeps Ballot visible outside hamburger only when the user has access
  // - Managers/admins can access Ballot for event support
  // =====================================================

  useEffect(() => {
    const checkVotedStatus = async () => {
      if (!user || !eventId) {
        setHasVotedInThisEvent(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        const data = userDoc.data();
        const voted = data?.votedEvents?.includes(eventId);

        setHasVotedInThisEvent(voted);
      } catch (err) {
        console.warn("Failed to check votedEvents:", err);
        setHasVotedInThisEvent(false);
      }
    };

    checkVotedStatus();
    }, [user, eventId]);

  return (
    <header className="lgv-nav-shell">

      {/* =====================================================
          Sticky Top Navigation
          Purpose:
          - Logo always left
          - Primary exposed links beside logo
          - Secondary links hidden into hamburger on smaller screens
          - Single hamburger opens the left nav drawer
      ===================================================== */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark lgv-sticky-topnav">
        <div className="container-fluid px-3">

          {/* 🏁 Logo */}
          <Link className="navbar-brand" to="/">
            <img
              src={LogoImage}
              alt="Let's Go Vote"
              style={{
                height: "60px",
                objectFit: "contain",
              }}
            />
          </Link>

          {/* =====================================================
              TOP NAV DISPLAY
              Purpose:
              - Logged out: Login / Register / Help
              - Logged in no event: Dashboard / Help + right desktop links
              - Logged in yes event: Event name linked to /{eventslug}
          ===================================================== */}

          <ul className="navbar-nav flex-row align-items-center me-auto">

            {!user && (
              <>
                <li className="nav-item me-3">
                  <Link className="nav-link" to="/login">Login</Link>
                </li>

                <li className="nav-item me-3">
                  <Link className="nav-link" to="/register">Register</Link>
                </li>

                <li className="nav-item me-3">
                  <Link className="nav-link" to="/help">Help</Link>
                </li>
              </>
            )}

            {user && !activeSlug && (
              <>
                <li className="nav-item me-3">
                  <Link className="nav-link" to="/dashboard">Dashboard</Link>
                </li>

                <li className="nav-item me-3">
                  <Link className="nav-link" to="/help">Help</Link>
                </li>
              </>
            )}

            {/* ==========================================================
                Event Banner
                - Shows current event while inside an event
                - Shows "Return to" banner when outside an event
            ========================================================== */}
            {(() => {
              let recentEvent = null;

              try {
                recentEvent = JSON.parse(localStorage.getItem("LGV_RECENT_EVENT"));
              } catch (err) {
                recentEvent = null;
              }

              // ----------------------------------------------------------
              // Current Event
              // ----------------------------------------------------------
              if (user && activeSlug) {
                return (
                  <li className="nav-item me-3">
                    <Link
                      className="lgv-event-home-badge"
                      to={`/${activeSlug}`}
                      title="Return to event home"
                    >
                      <span className="lgv-event-home-name">
                        {eventDoc?.name || "Event"}
                      </span>
                    </Link>
                  </li>
                );
              }

              // ----------------------------------------------------------
              // Recently Visited Event
              // ----------------------------------------------------------
              if (
                showRecentEvent &&
                recentEvent?.slug &&
                recentEvent?.name
              ) {
                  return (
                  <li className="nav-item me-3">
                    <div className="lgv-event-home-badge lgv-event-return-banner">

                      <Link
                        className="lgv-event-return-link"
                        to={`/${recentEvent.slug}`}
                      >
                        <span className="lgv-event-home-name">
                          {recentEvent.name}
                        </span>
                      </Link>

                      <button
                        type="button"
                        className="lgv-event-return-close"
                        onClick={() => {
                          localStorage.removeItem("LGV_RECENT_EVENT");
                          setShowRecentEvent(false);
                        }}
                      >
                        ❌ Close
                      </button>

                    </div>
                  </li>
                );
              }

              return null;
            })()}

          </ul>

          <ul className="navbar-nav flex-row align-items-center ms-auto d-none d-lg-flex">

            <li className="nav-item me-3">
              <Link className="nav-link" to="/help">Help</Link>
            </li>

            <li className="nav-item me-3">
              <Link className="nav-link" to="/#about">About</Link>
            </li>

            <li className="nav-item me-3">
              <Link className="nav-link" to="/#features">Features</Link>
            </li>

            <li className="nav-item me-3">
              <Link className="nav-link" to="/#pricing">Pricing</Link>
            </li>

            <li className="nav-item me-3">
              <Link className="nav-link" to="/#contact">Contact</Link>
            </li>

          </ul>

          {/* =====================================================
              SINGLE HAMBURGER
              - Only visible below lg breakpoint
              - Opens left nav drawer
          ===================================================== */}
          <button
            className="navbar-toggler d-lg-none"
            type="button"
            onClick={() => setManagerSidebarOpen((prev) => !prev)}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
        </div>
      </nav>

    {/* =====================================================
        Left Navigation Drawer / Desktop Sidebar
    ===================================================== */}
    {(managerSidebarOpen || isAdmin || activeSlug) && (
      <>
        <aside className={`lgv-manager-sidebar ${managerSidebarOpen ? "open" : ""}`}>

          <div className="lgv-manager-sidebar-title">
            Mission Control
          </div>

          {activeSlug && (
            <div className="lgv-sidebar-section">
              {(isAdmin || isManager) && (
                <SidebarSectionButton sectionKey="engagementTools" label="Engagement Tools" />
              )}

              {(!(isAdmin || isManager) || openSidebarSections.engagementTools) && (
                <>
                  {(hasVotedInThisEvent || isManager) && (
                    <Link className="lgv-sidebar-link" to={`/${activeSlug}/ballot`}>🗳 Ballot</Link>
                  )}

                  <Link className="lgv-sidebar-link" to="/dashboard">📊 Dashboard</Link>

                  {volunteersEnabled && (
                    <Link className="lgv-sidebar-link" to={`/${activeSlug}/volunteering`}>🙋 Volunteering</Link>
                  )}

                  {sponsorsEnabled && (
                    <Link className="lgv-sidebar-link" to={`/${activeSlug}/sponsors`}>🤝 Sponsors</Link>
                  )}

                  {vendorsEnabled && (
                    <Link className="lgv-sidebar-link" to={`/${activeSlug}/vendors`}>🏪 Vendors</Link>
                  )}

                  {auctionEnabled && (
                    <Link className="lgv-sidebar-link" to={`/${activeSlug}/auction`}>🏷 Auction</Link>
                  )}

                  {passportEnabled && (
                    <Link className="lgv-sidebar-link" to={`/${activeSlug}/passport`}>🧭 Passport</Link>
                  )}

                  {(storeEnabled || ticketsEnabled) && (
                    <Link className="lgv-sidebar-link" to={`/${activeSlug}/store`}>🛒 Store</Link>
                  )}

                  {sponsorsEnabled && (
                    <Link className="lgv-sidebar-link" to={`/${activeSlug}/my_sponsors`}>🎗 My Sponsors</Link>
                  )}

                  <Link className="lgv-sidebar-link" to="/help">❓ Help</Link>
                </>
              )}
            </div>
          )}

          {(isAdmin || (isManager && activeSlug)) && (
            <div className="lgv-sidebar-section">

              {isAdmin && (
                <div className="lgv-sidebar-section">
                  <SidebarSectionButton sectionKey="platform" label="Platform" />
                  {openSidebarSections.platform && (
                    <>
                      <Link className="lgv-sidebar-link" to="/events_list">🚀 Events</Link>
                      <Link className="lgv-sidebar-link" to="/organizers">🏢 Organizers</Link>
                      <Link className="lgv-sidebar-link" to="/demo_builder">🧪 Experience Engine</Link>
                      <Link className="lgv-sidebar-link" to="/sales_dashboard">📈 Prospect Engine</Link>
                      <Link className="lgv-sidebar-link" to="/promotion_dashboard">📣 Promotion Engine</Link>
                      <Link className="lgv-sidebar-link" to="/communication_dashboard">📣 Communication Engine</Link>
                      <Link className="lgv-sidebar-link" to="/seo_queue">🌐 SEO</Link>
                      <Link className="lgv-sidebar-link" to="/logs">📜 Logs</Link>
                    </>
                  )}
                </div>
              )}

              {activeSlug && (
                <>
                  {volunteersEnabled && (
                    <div className="lgv-sidebar-section">
                      <SidebarSectionButton sectionKey="volunteers" label="Volunteers" />
                      {openSidebarSections.volunteers && (
                        <>
                          <Link className="lgv-sidebar-link" to={`/${activeSlug}/volunteers`}>🙋 Volunteers</Link>
                          <Link className="lgv-sidebar-link" to={`/${activeSlug}/volunteer_settings`}>📋 Volunteer Content</Link>
                        </>
                      )}
                    </div>
                  )}

                  <div className="lgv-sidebar-section">
                    <SidebarSectionButton sectionKey="voting" label="Voting" />

                    {openSidebarSections.voting && (
                      <>
                        {judgeResultsEnabled && (
                          <Link
                            className="lgv-sidebar-link"
                            to={`/${activeSlug}/judge_results`}
                          >
                            🏆 Judge Results
                          </Link>
                        )}

                        <Link
                          className="lgv-sidebar-link"
                          to={`/${activeSlug}/categories`}
                        >
                          📂 Categories
                        </Link>
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/entry`}>🚗 Entries</Link>
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/users`}>👥 Voters</Link>
                        {scoringConfigurable && <Link className="lgv-sidebar-link" to={`/${activeSlug}/scoring`}>🧮 Scoring</Link>}
                        {auditEnabled && <Link className="lgv-sidebar-link" to={`/${activeSlug}/audit`}>🔍 Audit</Link>}
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/settings`}>🗳 Vote Settings</Link>
                      </>
                    )}
                  </div>

                  {sponsorsEnabled && (
                    <div className="lgv-sidebar-section">
                      <SidebarSectionButton sectionKey="sponsors" label="Sponsors" />
                      {openSidebarSections.sponsors && (
                        <>
                          <Link className="lgv-sidebar-link" to={`/${activeSlug}/sponsors_list`}>🤝 Sponsors</Link>
                          <Link className="lgv-sidebar-link" to={`/${activeSlug}/sponsors_settings`}>🎗 Sponsor Settings</Link>
                        </>
                      )}
                    </div>
                  )}

                  {vendorsEnabled && (
                    <div className="lgv-sidebar-section">
                      <SidebarSectionButton sectionKey="vendors" label="Vendors" />
                      {openSidebarSections.vendors && (
                        <>
                          <Link className="lgv-sidebar-link" to={`/${activeSlug}/vendors_list`}>🏪 Vendors</Link>
                          <Link className="lgv-sidebar-link" to={`/${activeSlug}/vendors_settings`}>🧾 Vendor Settings</Link>
                        </>
                      )}
                    </div>
                  )}

                  {auctionEnabled && (
                    <div className="lgv-sidebar-section">
                      <SidebarSectionButton sectionKey="auctions" label="Auctions" />
                      {openSidebarSections.auctions && (
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/auctions`}>🏷 Auctions</Link>
                      )}
                    </div>
                  )}

                  {/* =====================================================
                      📣 Promotion Engine
                      Purpose:
                      - Promotion workflow for this event
                      - Social Connections are managed under Settings
                      - Publishing uses Integration Engine
                  ===================================================== */}
                  {promotionEnabled && (
                    <div className="lgv-sidebar-section">
                      <SidebarSectionButton
                        sectionKey="promotions"
                        label="Promotion"
                      />

                      {openSidebarSections.promotions && (
                        <>
                          <Link
                            className="lgv-sidebar-link"
                            to={`/${activeSlug}/promotion_dashboard`}
                          >
                            📣 Dashboard
                          </Link>

                          <Link
                            className="lgv-sidebar-link"
                            to={`/${activeSlug}/promotion_studio`}
                          >
                            ✏️ Promotion Studio
                          </Link>

                          <Link
                            className="lgv-sidebar-link"
                            to={`/${activeSlug}/promotion_scheduling`}
                          >
                            📅 Scheduling
                          </Link>
                        </>
                      )}
                    </div>
                  )}

                  {passportNavigationVisible && (
                    <div className="lgv-sidebar-section">
                      <SidebarSectionButton sectionKey="passports" label="Passports" />
                      {openSidebarSections.passports && (
                        <>
                          <Link className="lgv-sidebar-link" to={`/${activeSlug}/event_intelligence`}>📊 Intelligence Dashboard</Link>
                          {passportEnabled && (
                            <Link className="lgv-sidebar-link" to={`/${activeSlug}/engagement`}>🧭 Engagement Dashboard</Link>
                          )}
                          {passportEnabled && (
                            <Link className="lgv-sidebar-link" to={`/${activeSlug}/engagement_activities`}>✅ Activities</Link>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {storeEnabled && (
                    <div className="lgv-sidebar-section">
                      <SidebarSectionButton sectionKey="store" label="Store" />
                      {openSidebarSections.store && (
                        <>
                          {storeEnabled && <Link className="lgv-sidebar-link" to={`/${activeSlug}/store_products`}>🛒 Products</Link>}
                          <Link className="lgv-sidebar-link" to={`/${activeSlug}/store_orders`}>📦 Store Orders</Link>
                          {ticketsEnabled && <Link className="lgv-sidebar-link" to={`/${activeSlug}/tickets`}>🎟 Tickets</Link>}
                          <Link className="lgv-sidebar-link" to={`/${activeSlug}/store_settings`}>⚙ Store Settings</Link>
                        </>
                      )}
                    </div>
                  )}

                  <div className="lgv-sidebar-section">
                    <SidebarSectionButton sectionKey="settings" label="Settings" />
                    {openSidebarSections.settings && (
                      <>
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/event_package`}>📦 Event Package & Power Ups</Link>
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/managers`}>🔐 Managers</Link>
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/scheduler`}>🗓 Scheduler</Link>
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/images`}>🖼 Images</Link>
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/map_settings`}>🗺 Map</Link>
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/public_links`}>🔗 QR Codes</Link>
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/content`}>📝 Event Content</Link>
                        <Link className="lgv-sidebar-link" to={`/${activeSlug}/event_settings`}>⚙ Event Settings</Link>
                        {socialConnectionsEnabled && (
                          <Link className="lgv-sidebar-link" to={`/${activeSlug}/integrations`}>🔌 Social Connections</Link>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="lgv-sidebar-section">
            <SidebarSectionButton sectionKey="sitePages" label="Site Pages" />

            {openSidebarSections.sitePages && (
              <>
                <Link className="lgv-sidebar-link" to="/help">❓ Help</Link>
                <Link className="lgv-sidebar-link" to="/guides">📘 Guides</Link>

                {isLanding && (
                  <>
                    <a className="lgv-sidebar-link" href="#info">ℹ️ About</a>
                    <a className="lgv-sidebar-link" href="#features">✨ Features</a>
                    <a className="lgv-sidebar-link" href="#pricing">💵 Pricing</a>
                    <a className="lgv-sidebar-link" href="#footer">📬 Contact</a>
                  </>
                )}

                {user && (
                  <Link className="lgv-sidebar-link" to="/logout">🚪 Logout</Link>
                )}
              </>
            )}
          </div>

        </aside>

        {managerSidebarOpen && (
          <button
            className="lgv-manager-sidebar-backdrop"
            type="button"
            aria-label="Close navigation"
            onClick={() => setManagerSidebarOpen(false)}
          />
        )}
      </>
    )}
    </header>
  )

};

export default ResponsiveNav;
